import { LegacyRef } from 'react';
import { Dictionary } from 'lodash';
import clsx from 'clsx';
import get from 'lodash/get';
import {
  useController,
  UseControllerProps,
  UseControllerReturn,
} from 'react-hook-form';
import {
  FormErrorMessage,
  FormLabel,
  FormControl,
  Input as ChakraInput,
  InputProps,
  InputGroup,
  InputRightElement,
  FormHelperText,
  FormControlProps,
  Text,
} from '@chakra-ui/react';
import TextMaskedInput, { MaskedInputProps } from 'react-text-mask';

import { shouldShowSuccess } from 'lib/form';
import FieldSuccessIcon from 'components/primatives/FieldSuccessIcon';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RhfTextFieldProps = UseControllerReturn<any, string> & {
  label: string;
  helpText?: string | JSX.Element;
  isRequired?: boolean;
  isPrivate?: boolean;
  showSuccess?: boolean;
  showError?: boolean;
  inputProps?: InputProps & Record<`data-${string}`, string>;
  wrapperProps?: FormControlProps;
  maskedInputProps?: MaskedInputProps;
};

export const RhfTextField = ({
  label,
  helpText,
  wrapperProps,
  inputProps,
  isRequired = false,
  isPrivate = false,
  showSuccess = true,
  showError = true,
  field,
  fieldState,
  formState,
  maskedInputProps,
}: RhfTextFieldProps) => {
  const defaultValue = get(formState.defaultValues, field.name, '');
  const hadDefaultValue = (defaultValue || '').length > 0;
  const isSuccess =
    showSuccess && shouldShowSuccess({ field, hadDefaultValue, fieldState });
  const isInvalid = showError && typeof fieldState.error !== 'undefined';

  const variant = isSuccess ? 'fancyValid' : 'fancy';

  const className = clsx(
    field.value && 'shrink-label',
    isSuccess && 'show-success',
  );

  return (
    <FormControl
      id={field.name}
      isRequired={isRequired}
      isInvalid={isInvalid}
      className={className}
      layerStyle="fieldWrapper"
      {...wrapperProps}
    >
      <InputGroup>
        {typeof maskedInputProps === 'object' ? (
          <TextMaskedInput
            data-dd-privacy="mask"
            {...field}
            value={field.value || ''}
            //   // We do this because "Types of property 'height' are incompatible".
            {...(inputProps as Dictionary<unknown>)}
            {...maskedInputProps}
            render={(ref, maskedInputProps) => (
              <ChakraInput
                ref={ref as LegacyRef<HTMLInputElement> | undefined}
                variant={variant}
                className={className}
                data-private={isPrivate ? 'data-private' : undefined}
                {...maskedInputProps}
              />
            )}
          />
        ) : (
          <ChakraInput
            data-dd-privacy="mask"
            variant={variant}
            className={className}
            {...field}
            value={field.value || ''}
            data-private={isPrivate ? 'data-private' : undefined}
            {...inputProps}
          />
        )}
        <FormLabel
          requiredIndicator={
            <Text as="span" layerStyle="requiredIndicator">
              *
            </Text>
          }
        >
          {label}
        </FormLabel>
        <InputRightElement>
          {isSuccess && <FieldSuccessIcon />}
        </InputRightElement>
      </InputGroup>
      {isInvalid && (
        <FormErrorMessage variant="fancyField">
          {fieldState.error?.message}
        </FormErrorMessage>
      )}
      {helpText && <FormHelperText>{helpText}</FormHelperText>}
    </FormControl>
  );
};

const RhfTextFieldContainer = ({
  label,
  inputProps,
  wrapperProps,
  isRequired,
  isPrivate,
  helpText,
  showSuccess,
  maskedInputProps,
  ...props // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: UseControllerProps<any> &
  Pick<
    RhfTextFieldProps,
    | 'label'
    | 'inputProps'
    | 'wrapperProps'
    | 'isRequired'
    | 'isPrivate'
    | 'helpText'
    | 'showSuccess'
    | 'maskedInputProps'
  >) => {
  const controler = useController(props);
  return (
    <RhfTextField
      label={label}
      inputProps={inputProps}
      wrapperProps={wrapperProps}
      isRequired={isRequired}
      isPrivate={isPrivate}
      showSuccess={showSuccess}
      helpText={helpText}
      maskedInputProps={maskedInputProps}
      {...controler}
    />
  );
};

export default RhfTextFieldContainer;
